import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLayout } from '../useLayout.js'
import PaneResizer, { startPaneResize, useStoredPaneWidth } from './PaneResizer.jsx'
import { SheetPresentationContext } from './Sheet.jsx'

const MIN_LIST_WIDTH = 280
const MAX_LIST_WIDTH = 400

function isEditableTarget(target) {
  return target instanceof Element && Boolean(target.closest(
    'input, textarea, select, [contenteditable]:not([contenteditable="false"])',
  ))
}

/**
 * One list and one detail, with the presentation decision kept out of routes.
 * The detail subtree stays in the same React position while the window crosses
 * 768px, so selection and unsaved component state survive the shell change.
 */
export default function AdaptiveListDetail({
  storageKey,
  listLabel,
  selectedId,
  list,
  detail,
  emptyLabel,
  toolbarAction,
  listFooter,
}) {
  const layout = useLayout()
  const rootRef = useRef(null)
  const listRef = useRef(null)
  const lastTriggerRef = useRef(null)
  const keyboardOpenRef = useRef(false)
  const arrowNavigationRef = useRef(false)
  const previousSelectionRef = useRef(selectedId)
  const previousPlatformRef = useRef(layout.platform)
  const [toolbarTarget, setToolbarTarget] = useState(null)
  const [listWidth, setListWidth] = useStoredPaneWidth(storageKey, {
    min: MIN_LIST_WIDTH,
    max: MAX_LIST_WIDTH,
    fallback: 320,
  })

  useEffect(() => {
    setToolbarTarget(layout.platform === 'mac'
      ? document.getElementById('mac-toolbar-view-actions')
      : null)
  }, [layout.platform])

  useEffect(() => {
    const previousSelection = previousSelectionRef.current
    if (layout.platform === 'mac' && selectedId && selectedId !== previousSelection && keyboardOpenRef.current) {
      window.requestAnimationFrame(() => {
        rootRef.current?.querySelector('.split-detail-surface')?.focus()
      })
    }
    if (!selectedId && previousSelection) {
      window.requestAnimationFrame(() => {
        const trigger = lastTriggerRef.current
        if (trigger?.isConnected) trigger.focus()
        else listRef.current?.focus()
      })
    }
    previousSelectionRef.current = selectedId
    keyboardOpenRef.current = false
  }, [layout.platform, selectedId])

  useEffect(() => {
    const previousPlatform = previousPlatformRef.current
    if (previousPlatform !== layout.platform && selectedId && layout.platform === 'mac') {
      window.requestAnimationFrame(() => {
        rootRef.current?.querySelector('.split-detail-surface')?.focus()
      })
    }
    previousPlatformRef.current = layout.platform
  }, [layout.platform, selectedId])

  useEffect(() => {
    const trigger = lastTriggerRef.current
    if (!selectedId && trigger && !trigger.isConnected && document.activeElement === document.body) {
      listRef.current?.focus()
    }
  })

  useEffect(() => {
    const triggers = Array.from(
      listRef.current?.querySelectorAll('[data-detail-id]') ?? [],
    )
    if (layout.platform !== 'mac') {
      triggers.forEach((trigger) => trigger.removeAttribute('tabindex'))
      return
    }

    const selectedKey = selectedId == null ? null : String(selectedId)
    const tabStop = triggers.find((trigger) => trigger.dataset.detailId === selectedKey)
      ?? triggers[0]
    triggers.forEach((trigger) => {
      trigger.tabIndex = trigger === tabStop ? 0 : -1
    })
  }, [layout.platform, list, selectedId])

  function rememberTrigger(event) {
    const trigger = event.target.closest?.('[data-detail-id]')
    if (!trigger || !event.currentTarget.contains(trigger)) return
    lastTriggerRef.current = trigger
    keyboardOpenRef.current = !arrowNavigationRef.current && event.detail === 0
  }

  function focusDetail() {
    window.requestAnimationFrame(() => {
      rootRef.current?.querySelector('.split-detail-surface')?.focus()
    })
  }

  function navigateList(event) {
    if (layout.platform !== 'mac' || isEditableTarget(event.target)) return
    if (!['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)) return

    const current = event.target.closest?.('[data-detail-id]')
    if (!current && event.target === event.currentTarget) {
      const tabStop = event.currentTarget.querySelector('[data-detail-id][tabindex="0"]')
      if (!tabStop) return
      event.preventDefault()
      tabStop.focus()
      return
    }
    if (!current || !event.currentTarget.contains(current)) return

    if (event.key === 'Enter') {
      event.preventDefault()
      const currentId = current.dataset.detailId
      if (selectedId != null && currentId === String(selectedId)) {
        focusDetail()
        return
      }
      keyboardOpenRef.current = true
      current.click()
      return
    }

    const triggers = Array.from(event.currentTarget.querySelectorAll('[data-detail-id]'))
      .filter((trigger) => (
        !trigger.disabled &&
        trigger.getAttribute('aria-disabled') !== 'true' &&
        trigger.getClientRects().length > 0
      ))
    if (!triggers.length) return

    event.preventDefault()
    const currentIndex = Math.max(0, triggers.indexOf(current))
    const nextIndex = selectedId == null && currentIndex === 0
      ? 0
      : Math.min(
        triggers.length - 1,
        Math.max(0, currentIndex + (event.key === 'ArrowDown' ? 1 : -1)),
      )
    const next = triggers[nextIndex]
    const nextId = next.dataset.detailId

    if (selectedId == null || nextId !== String(selectedId)) {
      arrowNavigationRef.current = true
      try { next.click() } finally { arrowNavigationRef.current = false }
    }
    window.requestAnimationFrame(() => {
      const liveTrigger = Array.from(
        listRef.current?.querySelectorAll('[data-detail-id]') ?? [],
      ).find((trigger) => trigger.dataset.detailId === nextId)
      liveTrigger?.focus()
    })
  }

  function resize(event) {
    if (layout.platform !== 'mac') return
    const left = rootRef.current?.getBoundingClientRect().left ?? 0
    startPaneResize(event, setListWidth, {
      min: MIN_LIST_WIDTH,
      max: MAX_LIST_WIDTH,
      measure: (moveEvent) => moveEvent.clientX - left,
    })
  }

  const mac = layout.platform === 'mac'

  return (
    <div
      className={'adaptive-list-detail ' + layout.platform}
      style={{ '--split-list-width': listWidth + 'px' }}
      ref={rootRef}
    >
      <section className="split-list-pane" aria-label={listLabel}>
        <div
          className="split-list-scroll"
          ref={listRef}
          tabIndex={-1}
          onClickCapture={rememberTrigger}
          onKeyDown={navigateList}
        >
          {list}
          {!mac ? toolbarAction : null}
          {listFooter}
        </div>
        <PaneResizer
          className="split-resizer"
          label={'Resize ' + listLabel.toLowerCase() + ' list'}
          min={MIN_LIST_WIDTH}
          max={MAX_LIST_WIDTH}
          value={listWidth}
          onPointerDown={resize}
        />
      </section>

      <section className="split-detail-pane" aria-label={listLabel + ' detail'}>
        {detail ? (
          <SheetPresentationContext.Provider value={mac ? 'inline' : 'overlay'}>
            {detail}
          </SheetPresentationContext.Provider>
        ) : mac ? (
          <div className="split-empty">{emptyLabel}</div>
        ) : null}
      </section>

      {mac && toolbarTarget && toolbarAction ? createPortal(toolbarAction, toolbarTarget) : null}
    </div>
  )
}
